import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useDASI, formatCurrency } from '../../context/DASIContext';
import './styles.scss';

// ── Vector status display ─────────────────────────────────────────────────────
const VectorItem = ({ vector }) => {
  const statusMap = {
    pass:       { icon: '✅', cls: 'td-passed',          label: '[Clean]' },
    recovery:   { icon: '💰', cls: 'td-recovery-alert',  label: '[RECOVERY ALERT]' },
    risk:       { icon: '⚠️', cls: 'td-risk-alert',      label: '[COMPLIANCE RISK]' },
    hitl:       { icon: '🤖', cls: 'td-hitl-alert',      label: '[HITL REQUIRED]' },
    cleared:    { icon: '✅', cls: 'td-passed',          label: '[Cleared]' },
    accepted:   { icon: '📝', cls: 'td-recovery-alert',  label: '[Logged for PSC]' },
    overridden: { icon: '✅', cls: 'td-passed',          label: '[False Positive]' },
  };
  const cfg = statusMap[vector.status] || statusMap.pass;
  return (
    <Box className={`td-vector-item ${cfg.cls}`}>
      <span className="td-v-icon">{cfg.icon}</span>
      <span className="td-v-label">{vector.name}</span>
      <span className="td-v-status">{cfg.label}</span>
    </Box>
  );
};

// ── Pillar chip colors ────────────────────────────────────────────────────────
const getPillarChips = (entry) => {
  const fail = (v) => ['recovery', 'risk', 'accepted'].includes(v.status);
  const p1 = entry.classVectors.some(fail) ? '#ef4444'
           : entry.classVectors.some((v) => v.status === 'hitl') ? '#f59e0b' : '#10b981';
  const p2 = entry.typeAVectors.some(fail) ? '#ef4444'
           : entry.typeAVectors.some((v) => v.status === 'hitl') ? '#f59e0b' : '#10b981';
  const p3 = entry.typeBVectors.some((v) => v.status === 'hitl') ? '#f59e0b'
           : entry.typeBVectors.some(fail) ? '#ef4444' : '#10b981';
  return [p1, p2, p3];
};

// ── Generate PSC email HTML for drawer ───────────────────────────────────────
const generateEmailHTML = (entry) => {
  let emailBodySnippets = '';
  let requiresPSC = false;

  entry.exceptions.forEach((ex) => {
    const isAccepted = ex.items.some((i) => i.resolutionMode === 'accept');
    if (!ex.cleared || isAccepted) {
      requiresPSC = true;
      emailBodySnippets += ex.emailSnippet;
    }
  });

  entry.hitl.forEach((h) => {
    if (h.resolutionMode === 'accept') {
      requiresPSC = true;
      const rawVal = h.resolutionReason.replace(/[^0-9.]/g, '');
      const parsedVal = parseFloat(rawVal);
      const fmt = isNaN(parsedVal)
        ? h.resolutionReason
        : parsedVal.toLocaleString('en-US', { minimumFractionDigits: 2 });
      emailBodySnippets += `<strong>[COMPLIANCE RISK] Undeclared Assist:</strong> A dutiable assist was omitted from the original entry. Please amend the entry to add <strong>$${fmt}</strong> to the total entered value under 19 CFR 152.103, and remit the resulting duty deficiency to CBP.<br><br>`;
    }
  });

  if (!requiresPSC) {
    return `<div style="text-align:center; padding: 24px;"><em><span style="color:#10b981; font-size: 18px; font-weight:bold;">✓</span><br><br>No PSC required for this entry. All compliance vectors successfully validated, or anomalies overridden as False Positives.</em></div>`;
  }
  return `<div style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 12px;"><strong>To:</strong> ${entry.emailTo}<br><strong>Subject:</strong> ${entry.emailSubject}</div>Please file a Post-Summary Correction (PSC) for Entry ${entry.id} based on the following findings:<br><br>${emailBodySnippets}Please amend the entry and confirm upon transmission.`;
};

// ── Override reasons ──────────────────────────────────────────────────────────
const EX_OVERRIDE_REASONS = ['False Positive', 'Offline Document on File'];
const HITL_OVERRIDE_REASONS = ['False Positive', 'Offline Document on File'];

// ── Exception accordion row ───────────────────────────────────────────────────
const ExceptionRow = ({ item, itemIdx, exIdx, entryIdx, exType, exSummary, resolve }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [overrideReason, setOverrideReason] = useState('False Positive');
  const [acceptVal, setAcceptVal] = useState('');

  const confirmed = item.cleared;
  const flagColor = exType === 'risk' ? '#991b1b' : '#166534';

  const handleConfirm = () => {
    if (mode === 'override' && overrideReason) {
      resolve(entryIdx, exIdx, itemIdx, 'override', overrideReason);
      setOpen(false);
    } else if (mode === 'accept') {
      resolve(entryIdx, exIdx, itemIdx, 'accept', acceptVal || '0');
      setOpen(false);
    }
  };

  return (
    <>
      <tr className={confirmed ? 'td-resolved-parent' : ''}>
        <td style={{ fontWeight: 600 }}>
          {confirmed ? <span className="td-strikethrough">{item.part}</span> : item.part}
        </td>
        <td className={confirmed && item.resolutionMode === 'accept' ? 'td-strikethrough' : ''}>
          {item.declared}
          {confirmed && item.resolutionMode === 'accept' && (
            <><br /><span style={{ color: '#10b981', textDecoration: 'none', fontWeight: 'bold', display: 'inline-block', marginTop: 4 }}>→ {item.resolutionReason}</span></>
          )}
        </td>
        <td>{item.target}</td>
        <td style={{ fontWeight: 700, color: flagColor }}>{item.flag}</td>
        <td>
          {confirmed ? (
            <span style={{ color: '#10b981', fontWeight: 600, fontSize: 12 }}>
              ✓ Resolved: {item.resolutionMode === 'override' ? item.resolutionReason : 'Accepted'}
            </span>
          ) : (
            <button
              className="td-btn-outline"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => setOpen((p) => !p)}
            >
              {open ? '▲ Close' : 'Resolve Exception ↓'}
            </button>
          )}
        </td>
      </tr>
      {open && !confirmed && (
        <tr className="td-child-row td-child-open">
          <td colSpan={5}>
            <div className="td-workspace-container">
              <p
                style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px 0' }}
                dangerouslySetInnerHTML={{ __html: exSummary }}
              />
              {!mode && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="td-btn-outline"
                    onClick={() => setMode('override')}
                  >
                    Override (False Positive)
                  </button>
                  <button className="td-primary-btn" onClick={() => setMode('accept')}>
                    Accept Error &amp; Log
                  </button>
                </div>
              )}
              {mode === 'override' && (
                <div>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>Select the reason for overriding this exception:</p>
                  <select
                    className="td-inline-select"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                  >
                    {EX_OVERRIDE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      className="td-primary-btn"
                      onClick={handleConfirm}
                    >
                      Confirm Override ✓
                    </button>
                    <button className="td-btn-outline" onClick={() => setMode(null)}>← Back</button>
                  </div>
                </div>
              )}
              {mode === 'accept' && (
                <div>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>
                    Enter the corrected value to log and include in PSC:
                  </p>
                  <input
                    className="td-inline-input"
                    type="text"
                    placeholder="e.g. corrected duty amount or HTS code"
                    value={acceptVal}
                    onChange={(e) => setAcceptVal(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      className="td-primary-btn"
                      onClick={handleConfirm}
                    >
                      Save Correction ✓
                    </button>
                    <button className="td-btn-outline" onClick={() => setMode(null)}>← Back</button>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── HITL Card ─────────────────────────────────────────────────────────────────
const HitlCard = ({ h, hIdx, entryIdx, resolveHitl }) => {
  const [mode, setMode] = useState(null);
  const [overrideReason, setOverrideReason] = useState('False Positive');
  const [acceptAmt, setAcceptAmt] = useState('');
  const resolved = h.resolutionMode !== null;

  const handleConfirm = () => {
    if (mode === 'override' && overrideReason) {
      resolveHitl(entryIdx, hIdx, 'override', overrideReason);
      setMode(null);
    } else if (mode === 'accept') {
      resolveHitl(entryIdx, hIdx, 'accept', acceptAmt || '0');
      setMode(null);
    }
  };

  // Compute resolved display values
  let badgeSty = {};
  let badgeText = 'Action Required';
  let resolvedDesc = h.desc;
  let resolvedTitle = h.title;

  if (resolved) {
    if (h.resolutionMode === 'accept') {
      const rawVal = h.resolutionReason.replace(/[^0-9.]/g, '');
      const parsedVal = parseFloat(rawVal);
      const fmt = isNaN(parsedVal)
        ? h.resolutionReason
        : parsedVal.toLocaleString('en-US', { minimumFractionDigits: 2 });
      badgeSty = { color: '#b91c1c', borderColor: '#b91c1c', background: '#fff1f2' };
      badgeText = `Exposure: $${fmt}`;
      resolvedTitle = h.title + ' ✓ Resolved';
      resolvedDesc = `[COMPLIANCE RISK] Undeclared Assist: A dutiable assist was omitted from the original entry. An amount of $${fmt} must be added to the entered value under 19 CFR 152.103.`;
    } else {
      badgeSty = { color: '#94a3b8', borderColor: '#cbd5e1', background: '#f8fafc' };
      badgeText = 'Exception Overridden';
      resolvedTitle = `${h.title} ✓ Resolved: ${h.resolutionReason}`;
      resolvedDesc = `Exception overridden. Reason: ${h.resolutionReason}`;
    }
  }

  const cardSty = resolved ? { background: '#f8fafc', borderColor: '#cbd5e1' } : {};
  const headerSty = resolved ? { background: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' } : {};

  return (
    <Box className="td-hitl-card" style={cardSty}>
      <Box className="td-hitl-header" style={headerSty}>
        <span>🤖 Agentic Predictor Action Card</span>
        <span className="td-hitl-badge" style={badgeSty}>{badgeText}</span>
      </Box>
      <Box className="td-hitl-body">
        <Typography className="td-hitl-title">{resolvedTitle}</Typography>
        <Typography className="td-hitl-desc">{resolvedDesc}</Typography>
        {!resolved && (
          <Box className="td-hitl-action-area">
            <strong style={{ color: '#b45309', fontSize: 13 }}>{h.actionTitle}</strong><br />
            <span style={{ color: '#92400e', fontSize: 12 }}>{h.actionDesc}</span>
            {!mode && (
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  className="td-btn-outline"
                  onClick={() => setMode('override')}
                >
                  Override
                </button>
                <button className="td-primary-btn" onClick={() => setMode('accept')}>
                  Accept &amp; Log Document
                </button>
              </div>
            )}
            {mode === 'override' && (
              <div style={{ marginTop: 12 }}>
                <select
                  className="td-inline-select"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                >
                  {HITL_OVERRIDE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    className="td-primary-btn"
                    onClick={handleConfirm}
                  >
                    Confirm Override ✓
                  </button>
                  <button className="td-btn-outline" onClick={() => setMode(null)}>← Back</button>
                </div>
              </div>
            )}
            {mode === 'accept' && (
              <div style={{ marginTop: 12 }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>Enter the amortized value of Assist:</p>
                <input
                  className="td-inline-input"
                  type="text"
                  placeholder="Enter the amortized value of Assist"
                  value={acceptAmt}
                  onChange={(e) => setAcceptAmt(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    className="td-primary-btn"
                    onClick={handleConfirm}
                  >
                    Save &amp; Log to PSC ✓
                  </button>
                  <button className="td-btn-outline" onClick={() => setMode(null)}>← Back</button>
                </div>
              </div>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const DASIAuditRadar = () => {
  const navigate = useNavigate();
  const { entries, currentIndex, setCurrentIndex, resolveExceptionItem, resolveHitl } = useDASI();
  const [activeTab, setActiveTab] = useState(0);

  // PSC button: 1=locked, 2=calculating, 3=ready
  const [pscState, setPscState] = useState(1);
  const resolvedRef = useRef({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const calcTimer = useRef(null);

  const entry = entries[currentIndex];

  const allExceptionsResolved = entry.exceptions.every((ex) => ex.cleared);
  const allHitlResolved = entry.hitl.every((h) => h.resolutionMode !== null);
  const allResolved = allExceptionsResolved && allHitlResolved;
  const hasItems = entry.exceptions.length > 0 || entry.hitl.length > 0;

  // PSC state machine: responds to entry change + resolution completeness
  useEffect(() => {
    clearTimeout(calcTimer.current);
    const key = entry.id;

    if (!allResolved) {
      setPscState(1);
      return;
    }
    if (!hasItems || resolvedRef.current[key]) {
      // Clean entry or already animated — show active immediately
      setPscState(3);
      return;
    }
    // First time fully resolved — animate
    setPscState(2);
    resolvedRef.current[key] = true;
    calcTimer.current = setTimeout(() => setPscState(3), 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allResolved, entry.id]);

  useEffect(() => () => clearTimeout(calcTimer.current), []);

  const handleSelectEntry = (index) => {
    setCurrentIndex(index);
    setActiveTab(0);
    setDrawerOpen(false);
  };

  const activeExCount = entry.exceptions.filter((ex) => !ex.cleared).length;
  const activeHitlCount = entry.hitl.filter((h) => h.resolutionMode === null).length;

  const badgeStyle = (count, color) => ({
    background: count > 0 ? color : '#cbd5e1',
    color: 'white',
    padding: '2px 6px',
    borderRadius: 10,
    fontSize: 10,
    marginLeft: 4,
    display: 'inline-block',
  });

  return (
    <Box className="td-ledger-wrapper">
      {/* ── Left: Entry Queue ────────────────────────────────────────────── */}
      <Box className="td-ledger-list">
        <Box className="td-ledger-queue-header">
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Entry Audit Queue</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {entries.filter((e) => e.exceptions.length > 0 || e.hitl.length > 0).length} Exceptions out of {entries.length}
          </Typography>
        </Box>

        {entries.map((e, index) => {
          const chips = getPillarChips(e);
          return (
            <Box
              key={e.id}
              className={`td-ledger-item${currentIndex === index ? ' td-selected' : ''}`}
              onClick={() => handleSelectEntry(index)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{e.id}</Typography>
              </Box>
              <Box sx={{ fontSize: 12, fontWeight: 700, marginBottom: '4px' }}>
                <span style={{ color: '#10b981' }}>Rec: {formatCurrency(e.recoveryValue)}</span>
                {' | '}
                <span style={{ color: '#ef4444' }}>Exp: {formatCurrency(e.deficiencyValue)}</span>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#64748b', marginBottom: '4px' }}>
                Date: {e.date} | Bkr: {e.broker}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px', marginTop: 1 }}>
                {chips.map((color, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      height: 18, minWidth: 26, borderRadius: 4, fontSize: 10, fontWeight: 700,
                      color: 'white', background: color, padding: '0 4px',
                    }}
                  >
                    P{i + 1}
                  </span>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Right: Dossier ───────────────────────────────────────────────── */}
      <Box className="td-ledger-detail">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography sx={{ margin: '0 0 4px 0', fontWeight: 700, fontSize: 22, color: '#1e293b' }}>
              Entry Dossier: {entry.id}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 14, marginTop: '4px' }}>
              Total Duty Recovery:{' '}
              <strong style={{ color: '#10b981' }}>{formatCurrency(entry.recoveryValue)}</strong>
              {' | '}
              Total Duty Exposure:{' '}
              <strong style={{ color: '#ef4444' }}>{formatCurrency(entry.deficiencyValue)}</strong>
            </Typography>
          </Box>

          {/* PSC Button */}
          {pscState === 1 ? (
            <button className="td-primary-btn td-psc-locked" disabled>
              Resolve Exceptions to Draft PSC →
            </button>
          ) : pscState === 2 ? (
            <button className="td-primary-btn td-psc-loading" disabled>
              ⚙️ Recalculating Duty...
            </button>
          ) : (
            <button
              className="td-primary-btn"
              style={{ background: '#166534' }}
              onClick={() => setDrawerOpen(true)}
            >
              Draft PSC Resolution Text →
            </button>
          )}
        </Box>

        {/* Tabs */}
        <Box className="td-dossier-tabs">
          {[
            { label: '1. Macro Overview (Matrix)', badge: null },
            { label: '2. Exception Details', badge: activeExCount > 0 ? { count: activeExCount, color: '#ef4444' } : null },
            { label: '3. HITL Actions', badge: activeHitlCount > 0 ? { count: activeHitlCount, color: '#f59e0b' } : null },
          ].map(({ label, badge }, i) => (
            <button
              key={i}
              className={`td-dossier-tab${activeTab === i ? ' td-tab-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {label}
              {badge && <span style={badgeStyle(badge.count, badge.color)}>{badge.count}</span>}
            </button>
          ))}
        </Box>

        {/* ── Tab 1: Macro Overview ──────────────────────────────────────── */}
        {activeTab === 0 && (
          <Box>
            <Box className="td-card">
              <Box className="td-card-header td-engine-pivot">
                <span>📊 Pillar 1: Pivot Row vs. 7501 Line Reconciliation</span>
              </Box>
              <Box className="td-card-body">
                <Box className="td-vector-grid">
                  {entry.classVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
                </Box>
              </Box>
            </Box>
            <Box className="td-card">
              <Box className="td-card-header td-engine-typeA">
                <span>🧮 Pillar 2: Deterministic Validation</span>
              </Box>
              <Box className="td-card-body">
                <Box className="td-vector-grid">
                  {entry.typeAVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Tab 2: Exception Accordion ────────────────────────────────── */}
        {activeTab === 1 && (
          <Box>
            {entry.exceptions.length === 0 ? (
              <Box className="td-empty-state">
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>No Exceptions Found</Typography>
                <Typography sx={{ fontSize: 14 }}>All line items reconciled flawlessly.</Typography>
              </Box>
            ) : (
              entry.exceptions.map((ex, exIdx) => {
                const allOverridden = ex.items.every((i) => i.resolutionMode === 'override');
                return (
                  <Box
                    key={exIdx}
                    className={`td-exception-container td-${ex.type}`}
                    sx={{ opacity: ex.cleared ? 0.75 : 1 }}
                  >
                    <Box className={`td-exception-header td-${ex.type}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{ex.type === 'recovery' ? '💰' : '⚠️'}&nbsp;&nbsp;{ex.title}</span>
                      <span style={{ fontWeight: 'bold', fontSize: 13 }}>
                        {!ex.cleared && ex.value > 0 && (
                          <span>{ex.type === 'recovery' ? 'Recovery' : 'Exposure'}: {formatCurrency(ex.value)}</span>
                        )}
                        {ex.cleared && allOverridden && ex.value > 0 && (
                          <span style={{ textDecoration: 'line-through', color: ex.type === 'recovery' ? 'var(--success-green, #10b981)' : 'var(--alert-red, #ef4444)', fontSize: 14 }}>
                            {ex.type === 'recovery' ? 'Recovery' : 'Exposure'}: {formatCurrency(ex.value)}
                          </span>
                        )}
                        {ex.cleared && allOverridden && ex.value === 0 && (
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>All Overridden</span>
                        )}
                        {ex.cleared && !allOverridden && (
                          <span style={{ color: '#10b981', fontSize: 12, fontWeight: 600 }}>✓ Fully Resolved</span>
                        )}
                      </span>
                    </Box>
                    <table>
                      <thead>
                        <tr>
                          <th>Part Number / Description</th>
                          <th>ACE 7501 Declared</th>
                          <th>Pivot / AI Target</th>
                          <th>Exception Flag</th>
                          <th>Resolution Workspace</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ex.items.map((item, itemIdx) => (
                          <ExceptionRow
                            key={itemIdx}
                            item={item}
                            itemIdx={itemIdx}
                            exIdx={exIdx}
                            entryIdx={currentIndex}
                            exType={ex.type}
                            exSummary={ex.summary}
                            resolve={resolveExceptionItem}
                          />
                        ))}
                      </tbody>
                    </table>
                  </Box>
                );
              })
            )}
          </Box>
        )}

        {/* ── Tab 3: HITL Cards ─────────────────────────────────────────── */}
        {activeTab === 2 && (
          <Box>
            <Typography className="td-hitl-radar-header">
              Pillar 3: Predictive Threat Scans Executed
            </Typography>
            <Box className="td-vector-grid" sx={{ marginBottom: '32px' }}>
              {entry.typeBVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
            </Box>
            {entry.hitl.length === 0 ? (
              <Box className="td-empty-state">
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>No Action Required</Typography>
                <Typography sx={{ fontSize: 14 }}>No supply chain anomalies detected.</Typography>
              </Box>
            ) : (
              entry.hitl.map((h, hIdx) => (
                <HitlCard
                  key={hIdx}
                  h={h}
                  hIdx={hIdx}
                  entryIdx={currentIndex}
                  resolveHitl={resolveHitl}
                />
              ))
            )}
          </Box>
        )}
      </Box>

      {/* ── PSC Slide-out Drawer ─────────────────────────────────────────── */}
      <div className={`td-drawer-overlay${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <div className={`td-psc-drawer${drawerOpen ? ' open' : ''}`}>
        <div className="td-drawer-header">
          <h3 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8, color: 'white' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            Broker Resolution Desk
          </h3>
          <button
            className="btn-dismiss"
            onClick={() => setDrawerOpen(false)}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', cursor: 'pointer', padding: '4px 12px', borderRadius: 4, fontSize: 13 }}
          >
            ✕ Close / Cancel
          </button>
        </div>
        <div className="td-drawer-body">
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 0 }}>
            Review the precise Post-Summary Correction (PSC) instructions generated by your resolution decisions before sending to the broker.
          </p>
          <div
            className="td-email-box"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: 13, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: generateEmailHTML(entry) }}
          />
          <button
            className="td-primary-btn"
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => navigate('/broker-resolution-desk', { state: { entryIndex: currentIndex } })}
          >
            Send via Broker Resolution Desk →
          </button>
        </div>
      </div>
    </Box>
  );
};

export default DASIAuditRadar;
